/**
 * MarkAcceptedDialog — quote lifecycle Phase 1.
 *
 * Records an out-of-band quote acceptance (verbal / email / signed offline —
 * including Internal quotes, which never go through the public accept link).
 * On success the quote flips to 'Accepted', unlocking the "Create Work Order"
 * button in QuoteApprovalSection's Accepted branch. The backend leaves
 * AcceptedFromIp NULL so a manual acceptance stays distinguishable from a
 * portal one.
 */
import { useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, TextField,
} from '@mui/material';
import { markQuoteAccepted, type MarkAcceptedChannel } from '../../../services/quoteService';

/** Single source for the channel options — no magic strings in the JSX. */
const CHANNEL_OPTIONS: ReadonlyArray<{ value: MarkAcceptedChannel; label: string }> = [
  { value: 'verbal', label: 'Verbal' },
  { value: 'email', label: 'Email' },
  { value: 'signed-offline', label: 'Signed offline' },
];

interface Props {
  open: boolean;
  quoteKey: string;
  onClose: () => void;
  /** Called after a successful accept — the parent refetches the quote. */
  onAccepted: () => void;
}

export const MarkAcceptedDialog = ({ open, quoteKey, onClose, onAccepted }: Props) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [channel, setChannel] = useState<MarkAcceptedChannel>('verbal');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName(''); setEmail(''); setChannel('verbal'); setNote('');
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await markQuoteAccepted(quoteKey, {
        acceptedByName: name.trim(),
        acceptedByEmail: email.trim() || undefined,
        channel,
        note: note.trim() || undefined,
      });
      reset();
      onAccepted();
    } catch (err) {
      const resp = (err as { response?: { data?: { error?: string } } }).response;
      setError(resp?.data?.error
        ?? (err instanceof Error ? err.message : 'Failed to mark the quote accepted.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Mark Quote as Accepted</DialogTitle>
      <DialogContent>
        <TextField label="Who accepted" fullWidth required size="small" autoFocus
          value={name} onChange={(e) => setName(e.target.value)} sx={{ mt: 1 }}
          placeholder="Client contact or approver name" />
        <TextField label="Email (optional)" fullWidth size="small" type="email"
          value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mt: 2 }} />
        <TextField label="How was it accepted?" fullWidth required select size="small"
          value={channel}
          onChange={(e) => setChannel(e.target.value as MarkAcceptedChannel)}
          sx={{ mt: 2 }}>
          {CHANNEL_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField label="Note (optional)" fullWidth multiline minRows={2} size="small"
          value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 2 }}
          placeholder="e.g. PO number, call details" />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}
          disabled={submitting || !name.trim()}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
          {submitting ? <CircularProgress size={18} /> : 'Mark as Accepted'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
